import axios from 'axios';
import { clientSafeLedgerCsv } from './clientPricingDisplay';
import { invoicePdfParams } from './invoiceFormat';
import { rankOrderSearchResults } from './orderIdentifier';
export {
  buildMetafieldMatch,
  buildOrderMetafieldTestPayload,
  buildPoMetafieldMatch,
  getMetafieldDisplayKey,
  getMetafieldOptionValue,
  getMetafieldRowKey,
  normalizeTestMappingResult,
  parseMetafieldKey,
} from './metafieldMapping';
axios.defaults.withCredentials = true;

// When an admin impersonates a client the backend issues a short-lived JWT
// (returned in the response body, not via Set-Cookie). All /client/* endpoints
// authenticate via the SKUSAVVY cookie, which is bound to the API domain and
// cannot be overwritten by frontend JS in a cross-origin setup.
//
// Fix: route every /client/* call through the local CRA dev-server proxy at
// /proxy-client/* (see src/setupProxy.js). The proxy injects
// Cookie: SKUSAVVY=<token> server-side before forwarding to the real API, so
// the backend always sees the right credential.
axios.interceptors.request.use((config) => {
  try {
    const token = sessionStorage.getItem('skusavvy_client_token');
    if (token && config.url) {
      const apiBase = process.env.REACT_APP_API || '';
      const clientPrefix = `${apiBase}/client/`;
      if (config.url.startsWith(clientPrefix)) {
        const path = config.url.slice(clientPrefix.length);
        config.url = `/proxy-client/${path}`;
        config.headers = config.headers ?? {};
        config.headers['x-impersonation-token'] = token;
      }
    }
    // In development the app runs on a *.replit.dev domain while the API lives
    // on *.skusavvy.com — a cross-site pair, so browsers (especially inside the
    // Replit preview iframe) refuse to store the SKUSAVVY auth cookie as a
    // third-party cookie and login silently fails. Rewrite every absolute API
    // URL to the same-origin /api/* path served by the CRA dev-server proxy
    // (see src/setupProxy.js), which forwards to the real backend. The cookie
    // then belongs to the dev domain (first-party) and auth works everywhere.
    // Production builds are untouched: NODE_ENV !== 'development' there.
    if (process.env.NODE_ENV === 'development' && config.url) {
      const apiBase = process.env.REACT_APP_API || '';
      if (/^https?:\/\//.test(apiBase) && config.url.startsWith(`${apiBase}/`)) {
        config.url = `/api${config.url.slice(apiBase.length)}`;
      }
    }
  } catch (e) {}
  return config;
});

export const normalizeApiError = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;
  const base = { success: false, _status: status };
  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const retryAfter = Number(data?.retryAfter ?? retryAfterHeader);
  const withRateLimit =
    status === 429 && Number.isFinite(retryAfter) && retryAfter > 0 ? { retryAfter } : {};
  // When the server returns a non-JSON body (HTML 404 page, plain text, etc.)
  // spreading the string would produce `{0:'<',1:'!',...}` and lose the message.
  // Extract a useful message instead.
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) {
      // Express default 404 page wraps the reason in <pre>Cannot POST /path</pre>.
      const m = trimmed.match(/<pre>([^<]+)<\/pre>/i);
      return {
        ...base,
        ...withRateLimit,
        message: m
          ? m[1].trim()
          : status === 404
            ? 'Endpoint not found on the server.'
            : `Server error (${status || 'no status'}).`,
      };
    }
    return { ...base, ...withRateLimit, message: trimmed.slice(0, 200) };
  }
  if (data && typeof data === 'object') {
    const errorMessage =
      typeof data.error === 'string'
        ? data.error
        : Array.isArray(data.errors)
          ? data.errors
              .map((item) => (typeof item === 'string' ? item : item?.message))
              .filter(Boolean)
              .join('; ')
          : null;
    return {
      ...base,
      ...data,
      ...withRateLimit,
      success: false,
      message:
        (typeof data.message === 'string' && data.message.trim()) ||
        errorMessage ||
        `Server error (${status || 'no status'}).`,
    };
  }
  return {
    ...base,
    ...withRateLimit,
    message: error.message || 'Unable to reach the server. Check your connection and try again.',
  };
};

const errData = normalizeApiError;

export const getApiError = (data) => {
  if (data?._status === 401) return 'Your session has expired. Please sign in again.';
  if (data?._status === 403) return 'You do not have access to this store.';
  if (data?._status === 429) return data.message || 'Too many requests. Please wait and try again.';
  return data?.message || 'Something went wrong. Please try again.';
};

export const parseRateLimit = (res) => {
  if (res?._status !== 429) return null;
  const retryAfter = Number(res.retryAfter);
  return {
    message: res.message || 'Too many requests. Please wait and try again.',
    // Do not replace a server supplied Retry-After with an arbitrary client
    // delay. The API contract always supplies a positive value for 429s.
    retryAfter: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
  };
};

export const login = async (data) => {
  try {
    const loginRes = await axios.post(`${process.env.REACT_APP_API}/auth/login`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return loginRes.data;
  } catch (error) {
    return errData(error);
  }
};

// POST /api/auth/forgot-password — public self-service reset request.
// Always returns 200 { ok: true } whether or not the email is registered
// (anti-enumeration), so show the same confirmation regardless.
export const forgotPassword = async (email) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/auth/forgot-password`,
      { email },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// POST /api/auth/reset-password-token — completes the emailed-link reset.
// On 200 the backend sets the SKUSAVVY session cookie: the user is logged in.
export const resetPasswordWithToken = async (token, newPassword) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/auth/reset-password-token`,
      { token, newPassword },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// POST /api/auth/change-password — authenticated self-service password change.
export const changePassword = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/auth/change-password`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const resetPassword = async (data) => {
  try {
    const resetRes = await axios.post(`${process.env.REACT_APP_API}/user/reset-password`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return resetRes.data;
  } catch (error) {
    return errData(error);
  }
};

export const isAuthenticate = async () => {
  try {
    const authRes = await axios.get(`${process.env.REACT_APP_API}/auth/isAuth`);
    return authRes.data;
  } catch (error) {
    return errData(error);
  }
};

export const logout = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/auth/logout`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

/**
 * Legacy dashboard routes — §18 (legacy dashboard).
 * These call GET|POST /main/* which is still documented and supported.
 * Intentionally retained: the admin dashboard reads aggregated vendor data
 * from these endpoints; no modern /admin/* equivalent exists yet.
 */
export const getReportData = async (storeId, clientType = 'vendor') => {
  try {
    const params = clientType && clientType !== 'vendor' ? { clientType } : {};
    const res = await axios.get(`${process.env.REACT_APP_API}/main/${storeId}`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const syncReportData = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/main/resync/${storeId}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getDetailsByVendorID = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/main/vendor`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getDetailsByProductID = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/main/product`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

/**
 * Legacy store management routes — §7 (legacy stores).
 * These call GET|POST /store* which is still supported by the backend.
 * Intentionally retained: used by the store admin UI; the modern
 * /admin/stores/:id PATCH routes (updateAdminStoreIdentity, etc.) handle
 * per-field updates but the full CRUD still goes through the legacy /store path.
 */
export const getStoresData = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/store`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const addNewStore = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/store`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateStore = async (data) => {
  try {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([k, v]) => {
      if (v != null) params.append(k, String(v));
    });
    const res = await axios.post(`${process.env.REACT_APP_API}/store/update`, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateAdminStoreIdentity = async (storeId, name, billingFromEmail) => {
  try {
    const body = { name };
    if (billingFromEmail !== undefined) body.billingFromEmail = billingFromEmail;
    const res = await axios.patch(`${process.env.REACT_APP_API}/admin/stores/${storeId}`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateAdminStoreFooter = async (storeId, invoiceFooter) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}`,
      { invoiceFooter: invoiceFooter ?? null },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Reconcile the local Shopify connection list against SKUSavvy before returning
// it. A failed verification response is intentionally returned unchanged so
// callers can preserve their last known-good list and inspect its code/status.
export const getShopifyConnections = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/shopify-connections`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Disconnect one Shopify connection. The backend also reconciles the remaining
// connections; callers should only replace their displayed list on a
// successful response containing the returned connections array.
export const disconnectShopifyConnection = async (storeId, connectionId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/shopify-connections/${encodeURIComponent(connectionId)}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateStoreBrandColors = async (storeId, primary, accent) => {
  try {
    const body = {};
    if (primary !== undefined) body.primary = primary;
    if (accent !== undefined) body.accent = accent;
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/brand`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Upload a logo image for a store.
// POST multipart/form-data to /admin/stores/:storeId/logo
// Expected response: { logoUrl: "https://..." }
export const uploadStoreLogo = async (storeId, file) => {
  try {
    const formData = new FormData();
    formData.append('logo', file);
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/logo`,
      formData,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Remove the logo for a store.
// DELETE /admin/stores/:storeId/logo
export const deleteStoreLogo = async (storeId) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/stores/${storeId}/logo`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Retired endpoint: never fall back to the former single-click deletion route.
export const deleteStore = async () => ({
  success: false,
  _status: 410,
  code: 'STORE_REMOVAL_PREVIEW_REQUIRED',
  message:
    'Store removal requires an upgrade to the preview-and-confirm flow. Open the store danger zone.',
});

const storeRemovalUrl = (storeId) =>
  `${process.env.REACT_APP_API}/admin/stores/${encodeURIComponent(storeId)}/removal`;

// These deliberately reject on transport errors so a lost response cannot be
// confused with a definitive server refusal.
export const previewStoreRemoval = async (storeId) =>
  (await axios.post(`${storeRemovalUrl(storeId)}-preview`, {})).data;

export const confirmStoreRemoval = async (storeId, approval) =>
  (await axios.post(storeRemovalUrl(storeId), approval, { timeout: 120000 })).data;

export const getStoreRemovalStatus = async (storeId) =>
  (await axios.get(storeRemovalUrl(storeId))).data;

/**
 * User management routes — §27 (users).
 * These call GET|POST /user* which is still documented and supported.
 * Intentionally retained: the user admin page uses these routes for full
 * CRUD; no /admin/users/* equivalent exists.
 */
export const getUsersData = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/user`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const addNewUser = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/user`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Re-send a client-user invite: fresh temp password, old one invalidated.
// Returns { ok, inviteEmailSent, message, [inviteEmailError], [tempPassword] }.
export const resendUserInvite = async (userId) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/user/${userId}/resend-invite`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Cancel a pending client-user invite without deleting the user account.
export const cancelUserInvite = async (userId) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/user/${userId}/cancel-invite`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateUser = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/user/update`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteUser = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/user/delete`, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminClients = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/clients`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Authoritative, store-wide storage fee evidence. Do not substitute
// occupancy-history calls here: that endpoint is scoped to one selected client.
export const getAdminStorageFeeBreakdown = async (storeId, month) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/storage-fee-breakdown`,
      { params: { month } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createAdminClient = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/clients`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateAdminClient = async (id, data) => {
  try {
    const res = await axios.put(`${process.env.REACT_APP_API}/admin/clients/${id}`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteAdminClient = async (id) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/clients/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminApiKeys = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/api-keys`, {
      params: storeId ? { storeId } : {},
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createAdminApiKey = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/api-keys`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteAdminApiKey = async (id) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/api-keys/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const refreshAdminOrder = async (orderId, storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/orders/${orderId}/refresh`,
      storeId ? { storeId } : {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminClientSyncStatus = async (clientId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/sync-status`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Legacy per-client assignment status. New store-wide sync surfaces use
// GET /api/admin/assignment-status with a storeId query parameter.
export const getAdminClientAssignmentStatus = async (clientId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/assignment-status`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const triggerAdminClientSync = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/clients`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

// Lightweight, store-scoped worker/lock status used by sync controls. Keep this
// separate from clients-health, which is substantially more expensive.
export const getAdminSyncStatus = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/sync/status`, {
      params: { storeId },
    });
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

// Guarded cancellation always starts cooperatively in the UI. Hard mode is
// reserved for an explicit escalation after a cooperative 409.
export const cancelAdminSync = async (storeId, mode = 'cooperative') => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/cancel`,
      {
        storeId,
        mode,
        waitMs: mode === 'hard' ? 0 : 15000,
      },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

export const getAdminSkusavvyProducts = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/skusavvy/products`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminSkusavvyInventory = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/skusavvy/inventory`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getRateCards = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/ratecards`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createRateCard = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/ratecards`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateRateCard = async (id, data) => {
  try {
    const res = await axios.put(`${process.env.REACT_APP_API}/admin/ratecards/${id}`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Duplicates a rate card (all fee fields + carrier margin rules). The copy
// is never the store default and has no clients assigned. Returns the full
// new rateCard.
export const copyRateCard = async (id, name) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/ratecards/${id}/copy`,
      name ? { name } : {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteRateCard = async (id) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/ratecards/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Rate card scheduled changes — lifecycle: pending → executing → fired/failed/cancelled
// GET  /api/admin/ratecards/:id/schedules          — list all schedules for a rate card
// POST /api/admin/ratecards/:id/schedules          — create a new pending schedule
// DELETE /api/admin/ratecards/:id/schedules/:sid   — cancel a pending schedule

export const getRateCardSchedules = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/schedules`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createRateCardSchedule = async (rateCardId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/schedules`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const cancelRateCardSchedule = async (rateCardId, scheduleId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/schedules/${scheduleId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getCarrierRules = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/carrier-rules`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const saveCarrierRules = async (rateCardId, rules) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/carrier-rules`,
      { rules },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Synced carrier/service catalog for a store (eLogx release).
// Returns { services: [{ carrierCode, carrierName, serviceCode, serviceName }],
//           lastSyncDate } — lastSyncDate null means the catalog has never synced.
export const getCarrierServices = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/carrier-services`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteCarrierRule = async (rateCardId, ruleId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/carrier-rules/${ruleId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Browsers derive File.type from OS file-association metadata, not from the
// file's actual extension/content. On many systems (especially CSVs saved by
// Excel, or files with no registered app) this comes through as an empty
// string or an unexpected type like "application/vnd.ms-excel" instead of
// "text/csv". If the backend's upload middleware whitelists mimetypes, those
// otherwise-valid .csv/.md/.txt files get rejected even though the extension
// is correct. Normalize the MIME type from the extension before upload so the
// server always sees the type that matches what the user picked.
const EXT_MIME_MAP = {
  json: 'application/json',
  csv: 'text/csv',
  md: 'text/markdown',
  txt: 'text/plain',
};

const normalizeFileMime = (file) => {
  const ext = file.name?.split('.').pop()?.toLowerCase();
  const expectedType = EXT_MIME_MAP[ext];
  if (!expectedType || file.type === expectedType) {
    return file;
  }
  return new File([file], file.name, { type: expectedType, lastModified: file.lastModified });
};

export const importRateCard = async ({ storeId, file, text }) => {
  try {
    const formData = new FormData();
    formData.append('storeId', storeId);
    if (file) {
      formData.append('file', normalizeFileMime(file));
    } else {
      formData.append('text', text);
    }
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/rate-cards/import`, formData);
    return res.data;
  } catch (error) {
    console.error(
      'importRateCard failed',
      error.response?.status ?? '(no status — network/CORS error)',
      error.response?.data ?? error.message,
    );
    return errData(error);
  }
};

export const confirmRateCardImport = async ({
  storeId,
  draft,
  rateCardId,
  clientId,
  miscFeeTemplates,
  storageLocationRules,
  carrierRules,
}) => {
  try {
    const body = { storeId, draft, rateCardId, clientId };
    if (Array.isArray(miscFeeTemplates)) body.miscFeeTemplates = miscFeeTemplates;
    if (Array.isArray(storageLocationRules)) body.storageLocationRules = storageLocationRules;
    if (Array.isArray(carrierRules)) body.carrierRules = carrierRules;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/rate-cards/import/confirm`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Server-computed fee flow annotations for the rate card editor's Fee Logic
// view. Returns { success, rateCardId, rateCardName, flows: [...] } where each
// flow carries active / suppressedBy / effectiveValue states straight from the
// billing engine. 404s (endpoint not yet deployed) surface as success:false.
export const getRateCardFeeVisualization = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/rate-cards/${rateCardId}/fee-visualization`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Production occupancy migration review is deliberately fixed-scope and
// write-free. Keep the request body here so the page cannot accidentally send
// an arbitrary date range, source file, checksum, or apply/post flag.
export const previewAugust2026Occupancy = async () => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/preview-august-2026-occupancy`,
      {
        storeId: '019e2699-55aa-76eb-9036-c6e27023a9f7',
        fromDate: '2026-08-01',
        toDate: '2026-08-31',
      },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getRateCardExportUrl = (rateCardId, format) =>
  `${process.env.REACT_APP_API}/admin/rate-cards/${rateCardId}/export?format=${format}`;

export const getRateCardExportJson = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/rate-cards/${rateCardId}/export`,
      { params: { format: 'json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getFeeAudit = async (clientId, params) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/fee-audit`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getFeeAuditSummary = async (clientId, params) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/fee-audit/summary`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getFeeFlows = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/fee-flows`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getFulfillmentAudit = async (storeId, params) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/billing/fulfillment-audit`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const overrideFulfillmentAudit = async (id, { storeId, action, note }) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/fulfillment-audit/${id}/override`,
      { storeId, action, note },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const bulkDismissFulfillmentAudit = async (storeId, dryRun) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/fulfillment-audit/bulk-dismiss`,
      { storeId, dryRun },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// POST /admin/billing/fulfillment-audit/recover-unbilled
// Runs billing recovery on billed_unverified rows with no matching ledger entry.
// dryRun=true returns { success, count }; false returns { success, total, recovered, zero_fee, no_shipment, failed }.
export const recoverUnbilledFulfillmentAudit = async (storeId, dryRun) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/fulfillment-audit/recover-unbilled`,
      { storeId, dryRun },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// POST /admin/billing/fulfillment-audit/reclassify-unverified
// Re-inspects billed_unverified rows to retroactively determine fulfillment source.
// dryRun=true returns preview counts; false triggers async processing.
export const reclassifyUnverifiedFulfillmentAudit = async (storeId, dryRun) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/fulfillment-audit/reclassify-unverified`,
      { storeId, dryRun },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// GET /admin/billing/fulfillment-audit/reclassify-unverified/status
// Poll while the reclassify job is running: { success, running, total, processed }.
export const getReclassifyUnverifiedStatus = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/billing/fulfillment-audit/reclassify-unverified/status`,
      { params: { storeId } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getKitFeeAudit = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/billing/kit-fee-audit`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const refetchKitFeeAudit = async (id, storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/kit-fee-audit/${id}/refetch`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const manualCorrectKitFeeAudit = async (id, { storeId, componentCount }) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/kit-fee-audit/${id}/manual-correct`,
      { storeId, componentCount },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientLedgerSummary = async (clientId, month) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/ledger-summary`,
      { params: { month } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const searchVendors = async (storeId, search) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/vendors`, {
      params: { storeId, search },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const testMapping = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/clients/test-mapping`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientsHealth = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/clients/health`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const impersonateClient = async (clientId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/auth/impersonate`,
      { clientId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const endImpersonation = async () => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/auth/impersonate-end`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientDashboard = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/dashboard`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

const withInclusiveAdjustmentFilter = (params = {}) => {
  const eventType = String(params.eventType ?? '').trim();
  return !eventType || eventType === 'Adjustment' ? { ...params, includeReasonFees: true } : params;
};

export const getClientLedger = async (params = {}) => {
  try {
    // The client endpoint projects paired Ship accounting rows into
    // customer-visible rows before applying limit and offset. Preserve its
    // count and grouped quantities rather than rebuilding them from one page.
    const res = await axios.get(`${process.env.REACT_APP_API}/client/ledger`, {
      params: withInclusiveAdjustmentFilter(params),
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminClientLedger = async (clientId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/clients/${clientId}/ledger`, {
      params: withInclusiveAdjustmentFilter(params),
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const reviewRateCardFeeRecalculation = async (data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/uninvoiced-fee-recalculations/review`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const startRateCardFeeRecalculation = async (
  operationId,
  reviewToken,
  idempotencyKey,
  confirmPartialCoverage = false,
) => {
  try {
    const body = { reviewToken };
    if (confirmPartialCoverage === true) body.confirmPartialCoverage = true;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/uninvoiced-fee-recalculations/${operationId}/start`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getRateCardFeeRecalculation = async (operationId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/billing/uninvoiced-fee-recalculations/${operationId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getActiveRateCardFeeRecalculation = async (params) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/billing/uninvoiced-fee-recalculations/active`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Thin fetch of ledger rows for a client — no grouping supplement.
// Use for scoped lookups (e.g. search by orderId) where aggregation isn't needed.
export const getAdminLedgerRows = async (clientId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/clients/${clientId}/ledger`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Misc fee templates are always scoped to one saved rate card. Callers must
// provide both identifiers; omitting the card scope is intentionally not a
// supported store-wide fallback.
export const getMiscFeeTemplates = async (storeId, rateCardId, options = {}) => {
  try {
    const params = { storeId, rateCardId };
    if (options?.includeInactive) params.includeInactive = true;
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/misc-fee-templates`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createMiscFeeTemplate = async (storeId, rateCardId, data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/misc-fee-templates`, data, {
      params: { storeId, rateCardId },
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateMiscFeeTemplate = async (id, storeId, rateCardId, data) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/misc-fee-templates/${id}`,
      data,
      {
        params: { storeId, rateCardId },
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteMiscFeeTemplate = async (id, storeId, rateCardId) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/misc-fee-templates/${id}`, {
      params: { storeId, rateCardId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createAdminMiscFee = async (clientId, payload) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/billing/misc`,
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};
export const getAdminMasterLedger = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/ledger`, {
      params: withInclusiveAdjustmentFilter(params),
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

const toLedgerSearchResponse = (data) => ({
  ...data,
  results: data?.rows ?? data?.entries ?? data?.ledger ?? [],
  total: data?.total ?? data?.count ?? 0,
});

export const searchAdminLedgerRows = async (q, storeId, limit = 20, offset = 0) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/ledger`, {
      params: { search: q, storeId, limit, offset, includeReasonFees: true },
    });
    return toLedgerSearchResponse(res.data);
  } catch (error) {
    return errData(error);
  }
};

export const searchClientLedgerRows = async (q, limit = 20, offset = 0) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/ledger`, {
      params: { search: q, limit, offset, includeReasonFees: true },
    });
    return toLedgerSearchResponse(res.data);
  } catch (error) {
    return errData(error);
  }
};

// Replaces the total for a specific ledger row. The idempotency key must remain
// stable when the caller retries the exact same submission.
// data: { feeApplied } or { qty, unitFee }, description (required),
// optional effectiveDate ("now" | YYYY-MM-DD | ISO).
export const createLedgerAdjustment = async (ledgerId, data, idempotencyKey) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/ledger/${encodeURIComponent(ledgerId)}/adjustment`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Appends an equal-and-opposite Adjustment row without deleting or mutating the
// original. The server treats an already-existing reversal as an idempotent
// success, so callers should refresh their source view for either response.
export const reverseLedgerAdjustment = async (ledgerId, data = {}) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/ledger/${encodeURIComponent(ledgerId)}/reversal`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Downloads the admin ledger as CSV. params: storeId (required), plus
// optional clientId, eventType, fromDate, toDate. eventType=Adjustment is
// inclusive of manual and SKUSavvy reason-fee rows. Throws with _status=413
// when the result exceeds the row cap (narrow the date range).
export const downloadAdminLedgerCsv = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(withInclusiveAdjustmentFilter(params)).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  let url = `${process.env.REACT_APP_API}/admin/ledger/export?${qs.toString()}`;
  // Plain fetch bypasses the axios dev same-origin rewrite; replicate it here
  // so the auth cookie stays first-party in the dev preview.
  try {
    const apiBase = process.env.REACT_APP_API || '';
    if (
      process.env.NODE_ENV === 'development' &&
      /^https?:\/\//.test(apiBase) &&
      url.startsWith(`${apiBase}/`)
    ) {
      url = `/api${url.slice(apiBase.length)}`;
    }
  } catch (e) {}
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw Object.assign(new Error(`CSV export failed: ${res.status}`), { _status: res.status });
  }
  return res.blob();
};

export const downloadClientLedgerCsv = async (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(withInclusiveAdjustmentFilter(params)).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, v);
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  let url = `${process.env.REACT_APP_API}/client/ledger/export${suffix}`;
  const headers = {};
  // Plain fetch bypasses the axios interceptors above, so replicate the
  // /client/* impersonation-proxy rewrite and the dev same-origin rewrite here.
  try {
    const token = sessionStorage.getItem('skusavvy_client_token');
    const apiBase = process.env.REACT_APP_API || '';
    const clientPrefix = `${apiBase}/client/`;
    if (token && url.startsWith(clientPrefix)) {
      url = `/proxy-client/${url.slice(clientPrefix.length)}`;
      headers['x-impersonation-token'] = token;
    } else if (
      process.env.NODE_ENV === 'development' &&
      /^https?:\/\//.test(apiBase) &&
      url.startsWith(`${apiBase}/`)
    ) {
      url = `/api${url.slice(apiBase.length)}`;
    }
  } catch (e) {}
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) {
    throw Object.assign(new Error(`CSV export failed: ${res.status}`), { _status: res.status });
  }
  return new Blob([clientSafeLedgerCsv(await res.text())], { type: 'text/csv;charset=utf-8' });
};

export const reconcileOrder = async ({ storeId, orderId, mode, reason } = {}) => {
  try {
    const body = { orderId };
    if (storeId != null) body.storeId = storeId;
    if (mode != null) body.mode = mode;
    if (reason != null) body.reason = reason;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/reconcile-order`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getPostInvoiceWarnings = async (params = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/billing/post-invoice-warnings`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getStoreLedgerSummary = async (storeId, { fromDate, toDate } = {}) => {
  const params = { storeId, limit: 1, offset: 0 };
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  return getAdminMasterLedger(params);
};

// extra: { clientId?, rateCardId? } — response conditionally includes the
// matching override tiers (storeCard/client/clientCard) for what was sent.
export const getAdminPackaging = async (storeId, extra = {}) => {
  try {
    const params = { storeId };
    if (extra.clientId != null) params.clientId = extra.clientId;
    if (extra.rateCardId != null) params.rateCardId = extra.rateCardId;
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/packaging`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const syncAdminPackaging = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/packaging`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const upsertPackagingOverride = async (
  storeId,
  packagingId,
  overridePrice,
  clientId,
  rateCardId,
) => {
  try {
    const body = { storeId, packagingId, overridePrice };
    if (clientId != null) body.clientId = clientId;
    if (rateCardId != null) body.rateCardId = rateCardId;
    const url = `${process.env.REACT_APP_API}/admin/packaging/overrides`;
    const res = await axios.post(url, body, { headers: { 'Content-Type': 'application/json' } });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deletePackagingOverride = async (overrideId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/packaging/overrides/${overrideId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Audit existing Packaging ledger rows billed before store-level price
// overrides were tracked in row metadata. Read-only — pairs with
// backfillPackagingFees below. flaggedRows is capped at 100 server-side.
export const getPackagingChargeAudit = async (storeId, fromDate, toDate, clientId) => {
  try {
    const params = { storeId };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (clientId) params.clientId = clientId;
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/billing/packaging-charge-audit`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const backfillPackagingFees = async (storeId, fromDate, toDate, clientId) => {
  try {
    const body = { storeId, fromDate, toDate };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/backfill-packaging-fees`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Repair missing adjustment reasons for a store.
// POST /api/admin/billing/backfill-movement-reasons
// Body: { storeId, fromDate?, toDate? }
// Returns: { success, scanned, updated, billed, fromDate, toDate }
// 409 — a run is already in progress; 502 — SKUSavvy unreachable.
// Idempotent and safe to expose behind an admin "Repair reasons" button.
export const backfillMovementReasons = async (storeId, fromDate, toDate) => {
  try {
    const body = { storeId };
    if (fromDate) body.fromDate = fromDate;
    if (toDate) body.toDate = toDate;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/backfill-movement-reasons`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientInventory = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/inventory`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientInventoryStorageHistory = async (months = 12) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/inventory/storage-history`, {
      params: { months },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientProducts = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/products`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// mapClientProductOwner was removed: POST /client/products/owner is not a
// documented endpoint. The bulk-owner CSV workflow (POST /api/client/products/bulk-owner,
// §4) is the supported way to set product ownership from the client portal.

export const triggerRemapOrders = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/remap-orders`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

// Re-attribute transfer_orders with clientId=null using current mapping rules (§18).
// Used by the OwnershipMapping page's "Re-attribute Transfers" button to fix
// transfers that were received before client mapping was configured.
export const triggerRemapTransfers = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/remap-transfers`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

export const syncSkusavvyOrders = async (storeId, fromDate) => {
  try {
    const body = { storeId };
    if (fromDate) body.fromDate = fromDate;
    // prettier-ignore
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/sync/skusavvy-orders`, body, { headers: { 'Content-Type': 'application/json' } });
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

export const adminBackfillMovements = async (storeId, fromDate, toDate) => {
  try {
    const body = { storeId };
    if (fromDate) body.fromDate = fromDate;
    if (toDate) body.toDate = toDate;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-movements`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminBackfillOrderFields = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-order-fields`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminBackfillOrderTimeline = async (storeId, clientId) => {
  try {
    const body = { storeId };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-order-timeline`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAssignmentStatus = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/assignment-status`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminOrders = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/orders`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminOrderDetail = async (orderId, config = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/orders/${orderId}`, config);
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) return { success: false, cancelled: true };
    return errData(error);
  }
};

export const getAdminOrderTimeline = async (orderId, config = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/orders/${orderId}/timeline`,
      config,
    );
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) return { success: false, cancelled: true };
    return errData(error);
  }
};

export const getClientOrders = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/orders`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientOrderDetail = async (orderId, config = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/orders/${orderId}`, config);
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) return { success: false, cancelled: true };
    return errData(error);
  }
};

export const getClientOrderTimeline = async (orderId, config = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/client/orders/${orderId}/timeline`,
      config,
    );
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) return { success: false, cancelled: true };
    return errData(error);
  }
};

export const getClientTransferList = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/transfers`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientTransferDetail = async (transferOrderId, config = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/client/transfers/${transferOrderId}`,
      config,
    );
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) return { success: false, cancelled: true };
    return errData(error);
  }
};

export const getClientShipmentDetail = async (orderId, shipmentId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/client/orders/${orderId}/shipments/${shipmentId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientInventoryMovements = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/inventory/movements`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminClientMovements = async (clientId, params = {}) => {
  // Normalize: callers may pass either `billingStatus` or `status`; backend uses `status`.
  const { billingStatus, ...rest } = params ?? {};
  const finalParams = billingStatus != null ? { ...rest, status: billingStatus } : rest;
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/movements`,
      { params: finalParams },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientPurchases = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/purchases`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientProfile = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/profile`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateClientProfile = async (data) => {
  try {
    const res = await axios.put(`${process.env.REACT_APP_API}/client/profile`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientInvoices = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/invoices`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientInvoiceDetail = async (invoiceId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/invoices/${invoiceId}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

const downloadInvoicePdf = async (path, { rollup, links = false } = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}${path}`, {
      params: invoicePdfParams(rollup, { links }),
      responseType: 'blob',
    });
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    throw Object.assign(
      new Error(status ? `PDF download failed: ${status}` : 'PDF download failed.'),
      { _status: status },
    );
  }
};

export const downloadClientInvoicePdf = async (invoiceId, options) =>
  downloadInvoicePdf(`/client/invoices/${invoiceId}/pdf`, { ...options, links: true });

export const getPlaidLinkToken = async () => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/client/plaid/link-token`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const exchangePlaidToken = async (payload) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/client/plaid/exchange-token`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getPlaidAccounts = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/plaid/accounts`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const initiateInvoicePayment = async (invoiceId, plaidAccountId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/client/plaid/pay/${invoiceId}`,
      { plaidAccountId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const revokePlaidAccount = async (plaidAccountId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/client/plaid/accounts/${plaidAccountId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ── Admin Plaid receiving account ─────────────────────────────────────────────
export const getAdminPlaidAccount = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/plaid/account`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminPlaidReadiness = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/plaid/readiness`,
      { params: { live: true } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminPlaidLinkToken = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/plaid/link-token`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const exchangeAdminPlaidToken = async (storeId, publicToken, accountId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/plaid/exchange-token`,
      { publicToken, accountId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteAdminPlaidAccount = async (storeId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/plaid/account`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getWebhookStatus = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/webhooks/status`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const registerWebhookTopics = async (storeId, topics) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/webhooks/config`,
      { storeId, topics },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

export const updateWebhookSecret = async (storeId, secret) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/webhooks/secret`,
      { storeId, secret },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Clears the stored webhook signing secret so HMAC verification is disabled
// until a new secret is saved. Uses PUT { secret: null } as documented in §16.
// No DELETE route exists for this endpoint.
export const clearWebhookSecret = async (storeId) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/webhooks/secret`,
      { storeId, secret: null },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Deletes the old webhook registration from SKUSavvy and re-creates it
// pointing to the correct backend DOMAIN. Requires no body — the backend
// reads its own DOMAIN env var to build the new URL.
export const reverifyWebhooks = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/webhooks/reverify`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInventory = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/inventory`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminLocationOccupancyHistory = async (storeId, clientId, months = 12) => {
  try {
    // Admin storage contract: GET /admin/stores/:storeId/location-rules/occupancy-history.
    // The backend requires clientId and returns { success, clientId, months, history }.
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/occupancy-history`,
      { params: { clientId, months } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInventoryMovements = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/inventory/movements`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminProducts = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/products`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInventoryEvents = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/inventory/events`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInventoryValueHistory = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/inventory/value-history`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminOrderEvents = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/orders/events`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminTransferOrders = async (params = {}) =>
  getAdminOrders({ ...params, orderType: 'TransferOrder' });

export const getAdminTransferOrderEvents = async (params = {}) =>
  getAdminOrderEvents({ ...params, orderType: 'TransferOrder' });

export const getAdminTransferList = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/transfers`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminTransferDetail = async (transferOrderId, storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/transfers/${transferOrderId}`, {
      params: storeId ? { storeId } : undefined,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const assignTransferClient = async (transferOrderId, storeId, clientId) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/transfers/${transferOrderId}/client`,
      { storeId, clientId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminBulkOwnerUpload = async (storeId, file) => {
  try {
    const form = new FormData();
    form.append('storeId', storeId);
    form.append('file', file);
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/products/bulk-owner`, form);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const clientBulkOwnerUpload = async (file) => {
  try {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${process.env.REACT_APP_API}/client/products/bulk-owner`, form);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Kick off a full non-terminal order-status backfill (fire-and-forget 202).
// Progress is server-side only; poll the orders list afterwards.
export const backfillOrderStatuses = async (storeId, maxPasses) => {
  try {
    const params = { storeId };
    if (maxPasses != null) params.maxPasses = maxPasses;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-order-statuses`,
      null,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Sync warehouses from the WMS. A successful sync also prunes warehouses
// deleted in the WMS. Returns { success, message, upserted, total }.
export const syncWarehouses = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/warehouses`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const triggerOrderSync = async (storeId, params = {}) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/orders`,
      { storeId, ...params },
      { headers: { 'Content-Type': 'application/json' } },
    );
    // A 202 is an acknowledgement only. Preserve it so UI callers never
    // mistake acceptance of a background job for its completion.
    return { ...res.data, _status: res.status };
  } catch (error) {
    return errData(error);
  }
};

const rankOrderResultsInSearchResponse = (data, query, type) => {
  if (!data || (type && type !== 'orders')) return data;

  const rankedOrders = rankOrderSearchResults(data.orders, query);
  const rankedResults =
    type === 'orders' ? rankOrderSearchResults(data.results, query) : data.results;
  const rankedGroups = Array.isArray(data.groups)
    ? data.groups.map((group) =>
        group?.type === 'orders'
          ? { ...group, results: rankOrderSearchResults(group.results, query) }
          : group,
      )
    : data.groups;

  if (
    rankedOrders === data.orders &&
    rankedResults === data.results &&
    rankedGroups === data.groups
  ) {
    return data;
  }
  return {
    ...data,
    ...(Array.isArray(rankedOrders) ? { orders: rankedOrders } : {}),
    ...(Array.isArray(rankedResults) ? { results: rankedResults } : {}),
    ...(Array.isArray(rankedGroups) ? { groups: rankedGroups } : {}),
  };
};

export const adminSearch = async (q, storeId, type, limit, offset) => {
  try {
    const params = { q };
    if (storeId) params.storeId = storeId;
    if (type) params.type = type;
    if (limit != null) params.limit = limit;
    if (offset != null) params.offset = offset;
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/search`, { params });
    return rankOrderResultsInSearchResponse(res.data, q, type);
  } catch (error) {
    return errData(error);
  }
};

export const adminSearchAI = async (question, storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/search/ai`,
      { question, ...(storeId ? { storeId } : {}) },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const clientSearch = async (q, type, limit, offset) => {
  try {
    const params = { q };
    if (type) params.type = type;
    if (limit != null) params.limit = limit;
    if (offset != null) params.offset = offset;
    const res = await axios.get(`${process.env.REACT_APP_API}/client/search`, { params });
    return rankOrderResultsInSearchResponse(res.data, q, type);
  } catch (error) {
    return errData(error);
  }
};

export const getDocs = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/docs`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getDocPage = async (slug) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/docs/${slug}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const searchDocs = async (q) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/docs/search`, { params: { q } });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const clientSearchAI = async (question) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/client/search/ai`,
      { question },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminNaturalLanguageQuery = async (question, storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/query`,
      { question, ...(storeId ? { storeId } : {}) },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getMappingChannels = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/channels`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Returns the distinct shopify_store_id values found in the SKUSavvy product
// cache for this store, along with each one's product count and the client
// (if any) that has it in its shopify_store_names mapping rule.
// Shape: { success, shopifyStoreIds: [{ shopifyStoreId, productCount, linkedClient }] }
export const getMappingShopifyStores = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/shopify-store-ids`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getMappingProductOwners = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/product-owners`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getMappingOrderMetafields = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/order-metafields`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getMappingPoMetafields = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/po-metafields`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createAdminAdjustment = async (clientId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/billing/adjustment`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminBackfillInventorySnapshots = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-inventory-snapshots`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Re-runs the fee backfill script (scripts/backfill-all-fees.js) over all
// shipments/orders. Idempotent — skips already-billed shipments and protects
// invoice-locked rows. storeId is optional; omit to run across all stores.
export const adminBackfillAllFees = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-all-fees`,
      storeId ? { storeId } : {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminMiscList = async (clientId, params = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/billing/misc`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminRecalculateMovements = async (storeId, clientId) => {
  try {
    const body = { storeId };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-movements`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminRecalculateShipmentFees = async (storeId, clientId) => {
  try {
    const body = { storeId };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-shipment-fees`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminRecalculatePackFees = async (storeId, clientId) => {
  try {
    const body = { storeId };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-pack-fees`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const adminRecalculateBoxFees = async (storeId, clientId) => {
  try {
    const body = { storeId };
    if (clientId) body.clientId = clientId;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-box-fees`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const recalculateTransferFees = async (body = {}) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-transfer-fees`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInvoices = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/invoices`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const generateInvoice = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/invoices`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminInvoiceDetail = async (invoiceId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/invoices/${invoiceId}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateInvoiceStatus = async (invoiceId, status) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/invoices/${invoiceId}`,
      { status },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Generic invoice PATCH — supports notes on editable statuses and invoicedDate on Drafts.
export const updateInvoice = async (invoiceId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/invoices/${invoiceId}`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Cancels an invoice (Draft/Sent/Overdue only). Releases its ledger rows
// back to un-invoiced and deletes auto-generated top-up rows.
export const cancelInvoice = async (invoiceId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/invoices/${invoiceId}/cancel`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const recordInvoicePayment = async (invoiceId, { paymentMethod, paidAt } = {}) => {
  try {
    const body = { paymentMethod };
    if (paidAt) body.paidAt = paidAt;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/invoices/${invoiceId}/record-payment`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const sendAdminInvoice = async (invoiceId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/invoices/${invoiceId}/send`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const downloadAdminInvoicePdf = async (invoiceId, options) =>
  downloadInvoicePdf(`/admin/invoices/${invoiceId}/pdf`, options);

export const downloadClientFeeSchedule = async (clientId, storeId) => {
  const url = `${process.env.REACT_APP_API}/admin/clients/${clientId}/fee-schedule?storeId=${encodeURIComponent(storeId)}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let message = '';
    if (typeof res.text === 'function') {
      const raw = await res.text();
      try {
        const body = JSON.parse(raw);
        message =
          (typeof body?.message === 'string' && body.message.trim()) ||
          (typeof body?.error === 'string' && body.error.trim()) ||
          '';
      } catch (error) {
        message = raw.trim();
      }
    }
    if (!message) {
      message =
        res.status === 404
          ? 'No rate card found for this client.'
          : `Fee schedule download failed (${res.status}).`;
    }
    throw Object.assign(new Error(message), { _status: res.status });
  }
  return res.blob();
};

export const emailClientFeeSchedule = async (clientId, storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/fee-schedule/email`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getWebhookLogs = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/webhooks/logs`, {
      params: { storeId, ...params },
      paramsSerializer: (p) => {
        const usp = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return;
          if (Array.isArray(v)) {
            v.forEach((item) => {
              if (item !== undefined && item !== null && item !== '') usp.append(k, item);
            });
          } else {
            usp.append(k, v);
          }
        });
        return usp.toString();
      },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminSuperDashboard = async ({ shippingWindow, fresh } = {}) => {
  try {
    const params = {};
    if (shippingWindow) params.shippingWindow = shippingWindow;
    if (fresh) params.fresh = 'true';
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/dashboard/super`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminOwnerDashboard = async (storeId, { fresh } = {}) => {
  // GET /admin/dashboard/owner requires storeId; bail out early rather than
  // firing a param-less request the server can't serve.
  if (!storeId) return { success: false, message: 'storeId required' };
  try {
    const params = { storeId };
    if (fresh) params.fresh = 'true';
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/dashboard/owner`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminDashboardStoresSummary = async ({ fresh } = {}) => {
  try {
    const params = fresh ? { fresh: 'true' } : {};
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/dashboard/stores-summary`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientDashboardCharts = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/dashboard/charts`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientASNs = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/asn`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientASNDetail = async (id) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/asn/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const submitASN = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/client/asn`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const parseASNCSV = async (fileOrText) => {
  try {
    let res;
    if (typeof fileOrText === 'string') {
      // csvText path — JSON body so the backend can accept pasted CSV
      res = await axios.post(
        `${process.env.REACT_APP_API}/client/asn/csv`,
        { csvText: fileOrText },
        { headers: { 'Content-Type': 'application/json' } },
      );
    } else {
      // file path — multipart upload
      const form = new FormData();
      form.append('file', fileOrText);
      res = await axios.post(`${process.env.REACT_APP_API}/client/asn/csv`, form);
    }
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Bulk import carrier-billed shipping adjustments from a CSV. When dryRun is
// true the backend validates and previews matches without writing anything.
export const importShippingAdjustments = async ({ file, storeId, dryRun }) => {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('storeId', storeId);
    if (dryRun) form.append('dryRun', 'true');
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/shipping-adjustment/import`,
      form,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminASNs = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/asn`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminASNDetail = async (id) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/asn/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const approveASN = async (id, { confirmed = false } = {}) => {
  try {
    const body = confirmed ? { confirmed: true } : {};
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/asn/${id}/approve`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    const data = errData(error);
    // 409 with confirmationRequired is the preview step of the two-step
    // approve flow — surface it as a successful preview response so the UI
    // can render the SKUSavvy payload + unmappedSkus and ask for confirm.
    if (data?._status === 409 && data?.confirmationRequired) {
      return { ...data, success: true, _preview: true };
    }
    return data;
  }
};

export const patchAdminAsn = async (id, body) => {
  try {
    const res = await axios.patch(`${process.env.REACT_APP_API}/admin/asn/${id}`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const patchClientAsn = async (id, body) => {
  try {
    const res = await axios.patch(`${process.env.REACT_APP_API}/client/asn/${id}`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const rejectASN = async (id, rejectionReason) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/asn/${id}/reject`,
      { rejectionReason },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ─── Client outbound submissions ──────────────────────────────────────────────

export const getClientOutboundSubmissions = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/outbound-submissions`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientOutboundSubmission = async (id) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/outbound-submissions/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const parseOutboundCSV = async (fileOrText) => {
  try {
    let res;
    if (typeof fileOrText === 'string') {
      res = await axios.post(
        `${process.env.REACT_APP_API}/client/outbound-submissions/csv`,
        { csvText: fileOrText },
        { headers: { 'Content-Type': 'application/json' } },
      );
    } else {
      const form = new FormData();
      form.append('file', fileOrText);
      res = await axios.post(`${process.env.REACT_APP_API}/client/outbound-submissions/csv`, form);
    }
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createClientOutboundSubmission = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/client/outbound-submissions`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const editClientOutboundSubmission = async (id, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/client/outbound-submissions/${id}`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const cancelClientOutboundSubmission = async (id) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/client/outbound-submissions/${id}/cancel`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ─── Admin outbound submissions ────────────────────────────────────────────────

export const getAdminOutboundSubmissions = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/outbound-submissions`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminOutboundSubmission = async (id) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/outbound-submissions/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const approveOutboundSubmission = async (id, body = {}) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/outbound-submissions/${id}/approve`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    const data = errData(error);
    // 409 with confirmationRequired is the expected preview step — surface as
    // a successful preview response so the UI can render the payload and confirm.
    if (data?._status === 409 && data?.confirmationRequired) {
      return { ...data, success: true, _preview: true };
    }
    return data;
  }
};

export const rejectOutboundSubmission = async (id, rejectionReason) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/outbound-submissions/${id}/reject`,
      { rejectionReason },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminWarehouses = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/warehouses`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// On-demand warehouse list sync and pruning (§18).
// Fetches the current warehouse list from SKUSavvy for a store and prunes
// any stale entries. Use to refresh warehouse pickers without waiting for
// the 12-hour background cron.
export const syncAdminWarehouses = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/warehouses`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientWarehouses = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/client/warehouses`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientWarehouseAssignments = async (clientId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/warehouses`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateClientWarehouseAssignments = async (clientId, warehouseIds) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/warehouses`,
      { warehouseIds },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getVendorOrders = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/stores/${storeId}/vendor-orders`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminVendorOrders = async (storeId, params = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/vendor-orders`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getAdminVendorOrder = async (storeId, vendorOrderId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/vendor-orders/${vendorOrderId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const triggerOrderMetafieldBackfill = async (storeId, fromDate = null, toDate = null) => {
  try {
    const body = { storeId };
    if (fromDate) body.fromDate = fromDate;
    if (toDate) body.toDate = toDate;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-order-metafields`,
      body,
      { headers: { 'Content-Type': 'application/json' }, params: { storeId } },
    );
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' && data.length < 200 && data) ||
      null;
    return { success: false, _status: status, message };
  }
};

// Backfill the shopify_store_id column on products that originated from
// Shopify but were synced before that column existed. Synchronous endpoint —
// can take 15-45 seconds for ~500 products. Returns { updated, skipped, total }.
export const triggerShopifyStoreIdBackfill = async (storeId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-shopify-store-id`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' && data.length < 200 && data) ||
      'Backfill failed. Please try again.';
    return { success: false, _status: status, message };
  }
};

export const getTransferMetafields = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/mapping/transfer-metafields`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const triggerPoMetafieldBackfill = async (storeId, fromDate = null) => {
  try {
    const body = { storeId };
    if (fromDate) body.fromDate = fromDate;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/backfill-po-metafields`,
      body,
      { headers: { 'Content-Type': 'application/json' }, params: { storeId } },
    );
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' && data.length < 200 && data) ||
      null;
    return { success: false, _status: status, message };
  }
};

export const triggerTransferMetafieldBackfill = async (storeId, scope = 'unattributed') => {
  try {
    const body = { storeId, scope };
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/refresh-transfer-metafields`,
      body,
      { headers: { 'Content-Type': 'application/json' }, params: { storeId } },
    );
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' && data.length < 200 && data) ||
      null;
    return { success: false, _status: status, message };
  }
};

export const updateStoreSettings = async (storeId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/settings`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const savvybotAsk = async (body) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/savvybot/ask`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const savvybotGetSessions = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/savvybot/sessions`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const savvybotGetMessages = async (sessionId, params = {}) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/savvybot/sessions/${sessionId}/messages`,
      { params },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const savvybotCreateSession = async (body = {}) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/savvybot/sessions`, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getMetafieldKeys = async (storeId, objectType) => {
  try {
    const params = {};
    if (storeId) params.storeId = storeId;
    if (objectType) params.objectType = objectType;
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/metafield-keys`, { params });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getInvoiceIntegrity = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/billing/invoice-integrity`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getSavvybotActions = async (params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/savvybot/actions`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const syncMetafieldKeys = async (storeId) => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[syncMetafieldKeys] storeId:', storeId);
  }
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/metafield-keys/sync`,
      { storeId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' && data.length < 200 && data) ||
      null;
    return { success: false, _status: status, message };
  }
};

export const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const isValidUuidV4 = (v) => UUID_V4_RE.test(v ?? '');

export const getVendors = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/skusavvy/vendors`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const setStoreDefaultVendor = async (storeId, vendorId) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/vendor-default`,
      { vendorId },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getClientVendor = async (clientId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/clients/${clientId}/vendor`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const setClientVendor = async (clientId, vendorIds) => {
  try {
    const res = await axios.put(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/vendor`,
      { vendorIds },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getProductCategories = async (storeId) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/product-categories`, {
      params: { storeId },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createProductCategory = async (data) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/product-categories`, data, {
      headers: { 'Content-Type': 'application/json' },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getProductCategory = async (id) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/product-categories/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateProductCategory = async (id, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/product-categories/${id}`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteProductCategory = async (id) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/product-categories/${id}`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const previewCategoryMembers = async (id, body) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/product-categories/${id}/preview`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const addCategoryMembers = async (id, body) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/product-categories/${id}/members`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getCategoryPickerProducts = async (storeId, params = {}) => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/product-categories/products`, {
      params: { storeId, ...params },
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getCategoryFilterOptions = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/product-categories/filter-options`,
      { params: { storeId } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const removeCategoryMembers = async (id, body) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/product-categories/${id}/members`,
      { data: body, headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ── Storage Location Rules (eLogx per-location billing) ──────────────────────
// Rules resolve occupied bin names (pattern match) to a locationType + rate so
// the daily storage job can bill per occupied physical bin when a rate card
// uses storageMode = 'perLocation'.
// Rules are card-owned. rateCardId must be the persisted selected card.
export const getLocationRules = async (storeId, rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules`,
      { params: { rateCardId } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getLocationRuleShelves = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/shelves`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createLocationRuleShelf = async (storeId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/shelves`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateLocationRuleShelf = async (storeId, shelfId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/shelves/${shelfId}`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getLocationRuleShelfBins = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/shelves/bins`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Storage billing responses are intentionally normalized at the UI boundary.
// The storage endpoints have shipped a few equivalent collection keys while
// older stores are being migrated; callers should never treat a missing array
// as an error or accidentally render a mixed-card response as billable.
export const normalizeLocationRulesResponse = (response) => {
  const rules = Array.isArray(response)
    ? response
    : (response?.rules ?? response?.locationRules ?? []);
  return {
    ...response,
    rules: Array.isArray(rules) ? rules : [],
    success: response?.success !== false,
  };
};

export const normalizeStorageRecoveryResponse = (response) => ({
  ...response,
  legacyRules: Array.isArray(response?.legacyRules) ? response.legacyRules : [],
  assignedPendingRefresh: Array.isArray(response?.assignedPendingRefresh)
    ? response.assignedPendingRefresh
    : [],
  genuinelyUnmatched: Array.isArray(response?.genuinelyUnmatched)
    ? response.genuinelyUnmatched
    : [],
  recoveryQueue: Array.isArray(response?.recoveryQueue) ? response.recoveryQueue : [],
  rateCards: Array.isArray(response?.rateCards) ? response.rateCards : [],
  recoverySummary: response?.recoverySummary ?? {},
  success: response?.success !== false,
});

export const normalizeStoreBinsResponse = (response) => ({
  ...response,
  warehouses: Array.isArray(response?.warehouses) ? response.warehouses : [],
  bins: Array.isArray(response?.bins) ? response.bins : [],
  summary: response?.summary ?? {},
  success: response?.success !== false,
});

export const normalizeStorageMutationResults = (response) => ({
  ...response,
  results: Array.isArray(response?.results) ? response.results : [],
  errors: Array.isArray(response?.errors) ? response.errors : [],
  summary: response?.summary ?? {},
  success: response?.success !== false,
});

export const createLocationRule = async (storeId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateLocationRule = async (storeId, ruleId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/${ruleId}`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteLocationRule = async (storeId, ruleId, rateCardId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/${ruleId}`,
      { params: { rateCardId } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Bulk upsert a full set of storage location rules in one call (§36).
// rules: array of rule objects (pattern, locationType, rateAmount, etc.).
// Existing rules not in the array are left untouched; conflicts on pattern
// are resolved by the backend (update if id matches, create if new).
export const bulkUpsertLocationRules = async (storeId, rateCardId, rules) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/bulk`,
      { rateCardId, rules },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Occupied-but-unbilled bins: bins seen in occupancy snapshots that no
// location rule currently covers.
export const getUnresolvedLocations = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/unresolved`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getLegacyLocationRuleRecovery = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/legacy`,
    );
    return normalizeStorageRecoveryResponse(res.data);
  } catch (error) {
    return errData(error);
  }
};

export const assignLegacyLocationRules = async (storeId, assignments) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/legacy/assign`,
      { assignments },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const refreshLegacyLocationRules = async (storeId, clientIds, dryRun = false) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/location-rules/legacy/refresh`,
      { clientIds, ...(dryRun ? { dryRun: true } : {}) },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Dry-run simulation of the daily per-location storage billing run.
// Never writes ledger rows. clientId is optional and scopes the preview
// to a single client.
export const previewLocationBilling = async (storeId, clientId, chargedDate) => {
  try {
    const body = { storeId };
    if (clientId != null) body.clientId = clientId;
    if (chargedDate) body.chargedDate = chargedDate;
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/preview-location-billing`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Per-location bin-occupancy backfill for a store. dryRun=true responds
// synchronously with the eligible clients; dryRun=false responds 202 and runs
// in the background (poll status). A 429 means a run is already in progress.
export const runPerLocationBinsBackfill = async (storeId, dryRun) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/sync/perlocation-bins-backfill`,
      { storeId, dryRun: !!dryRun },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const getPerLocationBinsBackfillStatus = async (storeId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/sync/perlocation-bins-backfill/status`,
      { params: { storeId } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ── Bins (SKUSavvy warehouse locations) ──────────────────────────────────────
// Live bin inventory for a warehouse, with metafields and the billing
// classification each bin resolves to right now (same resolver the nightly
// billing pass uses). warehouseId is optional — the response includes the
// account's warehouses list plus the active warehouseId.
// rateCardId is optional: when provided the response's per-location rates use
// card-specific rules merged over store defaults (same merge as billing);
// when absent only store-level defaults apply.
export const getStoreBins = async (storeId, warehouseId, rateCardId) => {
  try {
    const params = {};
    if (warehouseId) params.warehouseId = warehouseId;
    if (rateCardId) params.rateCardId = rateCardId;
    const res = await axios.get(`${process.env.REACT_APP_API}/admin/stores/${storeId}/bins`, {
      params,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Bulk-edit metafields on EXISTING bins (re-tag billing_type, set/remove
// arbitrary keys). Max 100 entries per request; validation is all-or-nothing
// but the mutations themselves report per-bin results (updated/partial/failed).
export const patchBinMetafields = async (storeId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/bins/metafields`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Create bins on SKUSavvy (bin + metafields). Max 100 per request; existing
// names are skipped (this endpoint never updates). Per-bin results:
// created / skipped / failed / metafields_failed.
export const createBinsBulk = async (storeId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/stores/${storeId}/bins/bulk`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Closed-month monthly-peak review. The backend owns the exact occupancy
// evidence and returns a short-lived previewToken; the apply call below must
// receive that token and the admin-approved proposed total.
export const previewStorageRecalculation = async (storeId, rateCardId, billingMonth) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/preview-storage-recalculation`,
      { storeId, rateCardId, billingMonth },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const applyStorageRecalculation = async (
  storeId,
  rateCardId,
  billingMonth,
  previewToken,
  approvedTotal,
) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/billing/recalculate-storage`,
      {
        storeId,
        rateCardId,
        billingMonth,
        previewToken,
        approvedTotal,
      },
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// ── Adjustment Reason Fee Rules ──
// Bill inventory qty-change movements whose free-text reason matches a rule
// (case-insensitive exact match). Rules are attached to a RATE CARD, not a
// store: /admin/ratecards/:rateCardId/adjustment-reason-fees. clientId null =
// card-wide; a client-specific rule beats the card-wide rule. Restock reason
// fees are additive on top of the standard restock fee.
export const getAdjustmentReasonFees = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Distinct adjustment reasons actually observed on the card's store movements —
// used to power the reason-pattern suggestions dropdown.
export const getAdjustmentReasons = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees/reasons`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Audit rules against WMS-configured reasons. Returns
// { success, audited, rules, wmsReasons, mismatches:number[] } — mismatches
// are rule IDs whose pattern matches no WMS reason. audited:false + auditNote
// when the WMS is unreachable.
export const getAdjustmentReasonFeeAudit = async (rateCardId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees/audit`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const createAdjustmentReasonFee = async (rateCardId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const updateAdjustmentReasonFee = async (rateCardId, ruleId, data) => {
  try {
    const res = await axios.patch(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees/${ruleId}`,
      data,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

export const deleteAdjustmentReasonFee = async (rateCardId, ruleId) => {
  try {
    const res = await axios.delete(
      `${process.env.REACT_APP_API}/admin/ratecards/${rateCardId}/adjustment-reason-fees/${ruleId}`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Admin view of a client's linked Plaid bank accounts (§34).
// Used on the admin invoice detail page's payment section to show which
// bank account was used for ACH payments.
export const getAdminClientPlaidAccounts = async (clientId) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/plaid-accounts`,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Upload a logo image for a client (§24).
// POST multipart/form-data to /admin/clients/:id/logo
export const uploadClientLogo = async (clientId, file) => {
  try {
    const formData = new FormData();
    formData.append('logo', file);
    const res = await axios.post(
      `${process.env.REACT_APP_API}/admin/clients/${clientId}/logo`,
      formData,
    );
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Remove the logo for a client (§24).
// DELETE /admin/clients/:id/logo
export const deleteClientLogo = async (clientId) => {
  try {
    const res = await axios.delete(`${process.env.REACT_APP_API}/admin/clients/${clientId}/logo`);
    return res.data;
  } catch (error) {
    return errData(error);
  }
};

// Super-admin escape hatch: force-release a stuck startup sync lock.
// POST /api/admin/sync/force-clear-lock — returns 403 for non-super-admins.
export const forceClearSyncLock = async (storeId) => {
  try {
    const res = await axios.post(`${process.env.REACT_APP_API}/admin/sync/force-clear-lock`, {
      storeId,
    });
    return res.data;
  } catch (error) {
    return errData(error);
  }
};
